import React from 'react'
import moment from 'moment'
import { ECellColor } from '@/components/calendar/calendarTypes'
import Grid from '@mui/material/Grid'
import WeekPicker from '@/components/weekPicker/WeekPicker'
import { startOfISOWeek } from 'date-fns'
import ColorExplonationBox from '@/components/calendar/ColorExplonationBox'
import PopupDialog from '@/components/popupDialog/PopupDialog'
import CellTypeDropDown from '@/components/calendar/CellTypeDropDown'
import ActionButton from '@/components/buttons/ActionButton'
import SaveIcon from '@mui/icons-material/Save'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import DropDown, { IValue } from '@/components/dropDown/DropDown'
import PdfSchema from '@/components/pdfSchema/PdfSchema'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import {
  useLazyGetSchemaQuery,
  useLazyUpdateDayCommentQuery,
  IUser,
  IUpdateDay,
} from '@/store/apis/schemas'
import { ETimeslotType } from '@/store/apis/timeslots'
import { useLazyGetPlacesQuery } from '@/store/apis/places'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import BaseSchema from './BaseSchema'
import ScenesSchema from './ScenesSchema'
import SceneGroupsSchema from './SceneGroupsSchema'
import PreviewSchema from './PreviewSchema'

enum ESchemaType {
  BaseSchema = 0,
  Scenes = 1,
  SceneGroups = 2,
  PreviewSchema = 3,
}

const CreateFinalSchema: React.FC = () => {
  const { t } = useTranslation()
  const urlParams = useParams()
  const [
    getSchemaQuery,
    {
      data: schemaData,
      isFetching: isFetchingSchema,
      isLoading: isLoadingSchemaData,
    },
  ] = useLazyGetSchemaQuery()
  const [getPlacesQuery, { data: placesData }] = useLazyGetPlacesQuery()
  const [updateDayCommentQuery, { isFetching: isUpdatingDayData }] =
    useLazyUpdateDayCommentQuery()
  const [firstDay, setFirstDay] = React.useState<moment.Moment | undefined>()
  const [selectedId, setSelectedId] = React.useState<number | undefined>()
  const [dropDownData, setDropDownData] = React.useState<IValue[] | undefined>()
  const [schemaTypeData, setSchemaTypeData] = React.useState<
    IValue[] | undefined
  >()
  const [selectedSchemaTypeId, setSelectedSchemaTypeId] = React.useState<
    ESchemaType | undefined
  >(ESchemaType.Scenes)
  const [placeDropDownData, setPlaceDropDownData] = React.useState<
    IValue[] | undefined
  >()
  const [cellType, setCellType] = React.useState<ETimeslotType>(
    ETimeslotType.Required,
  )
  const [dayDatas, setDayDatas] = React.useState<IUpdateDay[]>([])
  const [onlyPrioritised, setOnlyPrioritised] = React.useState<boolean>(false)
  const [isPopupOpen, setIsPopupOpen] = React.useState<boolean>(false)

  React.useEffect(() => {
    setSchemaTypeData([
      {
        id: ESchemaType.BaseSchema,
        label: t('baseSchema'),
      },
      {
        id: ESchemaType.Scenes,
        label: t('scenes'),
      },
      {
        id: ESchemaType.SceneGroups,
        label: t('sceneGroups'),
      },
      {
        id: ESchemaType.PreviewSchema,
        label: t('preview'),
      },
    ])
  }, [])

  React.useEffect(() => {
    if (urlParams?.schemaId) {
      getSchemaQuery({ id: Number(urlParams.schemaId) })
      getPlacesQuery({ schemaId: Number(urlParams.schemaId) })
    }
  }, [getSchemaQuery, urlParams])

  React.useEffect(() => {
    if (placesData) {
      const newDropDownData = placesData.items.map((place) => ({
        id: place.id,
        label: place.name,
      }))
      setPlaceDropDownData(newDropDownData)
    }
  }, [placesData, setPlaceDropDownData])

  React.useEffect(() => {
    if (schemaData && !firstDay) {
      const nowDate = new Date()
      const schemaDate = new Date(schemaData.schema.schema.startDate)
      const newFirstDay =
        nowDate < schemaDate ? moment(schemaDate) : moment(nowDate)
      setFirstDay(newFirstDay.startOf('week'))
      handleChangeSchemaType(selectedSchemaTypeId)
    }
  }, [schemaData])

  const handleChangeSchemaType = (newType: ESchemaType): void => {
    if (newType === ESchemaType.Scenes) {
      schemaData.scenes.length && setSelectedId(schemaData.scenes[0].scene.id)
      const newDropDownData = schemaData?.scenes.map((scene) => ({
        id: scene.scene.id,
        label: `${scene.scene.name} | ${scene.users.length} | ${scene.timeslots.length}`,
      }))
      setDropDownData(newDropDownData)
    } else if (newType === ESchemaType.SceneGroups) {
      schemaData.sceneGroups.length &&
        setSelectedId(schemaData.sceneGroups[0].sceneGroup.id)
      const newDropDownData = schemaData?.sceneGroups.map((sceneGroup) => ({
        id: sceneGroup.sceneGroup.id,
        label: `${sceneGroup.sceneGroup.name} | ${sceneGroup.scenes.length} | ${sceneGroup.timeslots.length}`,
      }))
      setDropDownData(newDropDownData)
    } else if (
      newType === ESchemaType.BaseSchema ||
      newType === ESchemaType.PreviewSchema
    ) {
      setSelectedId(undefined)
    }

    setSelectedSchemaTypeId(newType)
  }

  const handleSaveDayComment = async (): Promise<void> => {
    Promise.all(
      dayDatas.map(async (dayData) => {
        updateDayCommentQuery({
          ...dayData,
          schemaId: schemaData.schema.schema.id,
          date: moment(new Date(dayData.date)).format('yyyy-MM-DD'),
        })
      }),
    )
    await getSchemaQuery({ id: schemaData.schema.schema.id })
    setDayDatas([])
  }

  let users: IUser[] = []
  if (schemaData && selectedId !== undefined) {
    if (selectedSchemaTypeId === ESchemaType.Scenes) {
      const scene = schemaData.scenes.find(
        (scene) => scene.scene.id === selectedId,
      )
      const foundUsers = scene.users.map((user) => {
        const foundIndex = schemaData.users.findIndex(
          (schemaUser) => schemaUser.id === user.id,
        )
        if (foundIndex !== -1) {
          return schemaData.users[foundIndex]
        }
      })
      const filteredUsers = foundUsers.filter((user) => {
        if (onlyPrioritised) {
          return (
            scene.scene.userPriorities?.findIndex(
              (userPriorityId) => userPriorityId === user.id,
            ) > -1
          )
        }
        return true
      })
      if (scene) {
        users = filteredUsers
      }
    } else {
      const sceneGroup = schemaData.sceneGroups.find(
        (sceneGroup) => sceneGroup.sceneGroup.id === selectedId,
      )
      if (sceneGroup) {
        users = sceneGroup.scenes.reduce((list, sceneGroupScene) => {
          const schemaScene = schemaData.scenes.find(
            (scene) => scene.scene.id === sceneGroupScene.id,
          )
          const foundUsers = schemaScene.users.map((user) => {
            const foundIndex = schemaData.users.findIndex(
              (schemaUser) => schemaUser.id === user.id,
            )
            if (foundIndex !== -1) {
              return schemaData.users[foundIndex]
            }
          })
          const filteredUsers = foundUsers.filter(
            (user) =>
              list.findIndex((listUser) => listUser.id === user.id) === -1,
          )
          list = list.concat(filteredUsers)
          return list
        }, [] as IUser[])
      }
    }
  }

  const lastDay = moment(firstDay).add(8, 'day')

  let pdfButtonLabel = t('buttons.exportAllToPdf')
  switch (selectedSchemaTypeId) {
    case ESchemaType.Scenes:
      pdfButtonLabel = t('buttons.exportSceneToPdf')
      break
    case ESchemaType.SceneGroups:
      pdfButtonLabel = t('buttons.exportSceneGroupToPdf')
      break
    default:
      pdfButtonLabel = t('buttons.exportAllToPdf')
      break
  }

  return (
    <>
      <Grid
        container
        columnSpacing={2}
        alignItems="center"
        justifyContent="center"
      >
        <Grid item xs>
          <WeekPicker
            value={firstDay ? firstDay.toDate() : new Date()}
            minDate={
              schemaData
                ? startOfISOWeek(new Date(schemaData.schema.schema.startDate))
                : startOfISOWeek(new Date())
            }
            maxDate={
              schemaData
                ? new Date(schemaData.schema.schema.endDate)
                : new Date()
            }
            onChange={(value) => setFirstDay(moment(value).startOf('week'))}
          />
        </Grid>
        <Grid
          item
          xs
          container
          display="flex"
          alignItems="center"
          direction="column"
        >
          <Grid item xs>
            <DropDown
              label={t('schemaType')}
              width="300px"
              isLoading={isFetchingSchema}
              defaultValue={
                schemaTypeData && schemaTypeData[selectedSchemaTypeId]
              }
              values={schemaTypeData}
              onChange={(newValue) => handleChangeSchemaType(newValue.id)}
            />
          </Grid>
          {selectedSchemaTypeId === ESchemaType.BaseSchema && (
            <Grid
              container
              item
              xs
              display="flex"
              alignItems="center"
              direction="column"
              spacing={2}
              marginBottom={2}
            >
              <Grid item xs>
                <CellTypeDropDown
                  defaultValue={cellType}
                  onChange={(newValue) => setCellType(newValue)}
                />
              </Grid>
              <Grid item xs>
                <ActionButton
                  icon={<SaveIcon />}
                  isLoading={isUpdatingDayData}
                  disabled={!dayDatas.length}
                  label={t('buttons.saveDayData')}
                  onClick={handleSaveDayComment}
                />
              </Grid>
            </Grid>
          )}
          {(selectedSchemaTypeId === ESchemaType.Scenes ||
            selectedSchemaTypeId === ESchemaType.SceneGroups) && (
            <>
              <Grid item xs>
                <DropDown
                  label={
                    selectedSchemaTypeId === ESchemaType.Scenes
                      ? t('scenes')
                      : t('sceneGroups')
                  }
                  subLabel={`${t('name')} | ${
                    selectedSchemaTypeId === ESchemaType.Scenes
                      ? t('userCount')
                      : t('sceneCount')
                  } | ${t('timeslotCount')} `}
                  defaultValue={dropDownData && dropDownData[0]}
                  width="300px"
                  values={dropDownData}
                  isLoading={isFetchingSchema}
                  onChange={(newValue) => setSelectedId(newValue.id)}
                />
              </Grid>
              <Grid item xs>
                <FormControlLabel
                  labelPlacement="top"
                  label={t('buttons.showOnlyPrioritised')}
                  control={
                    <Switch
                      color="primary"
                      disabled={isFetchingSchema}
                      checked={onlyPrioritised}
                      onClick={() => setOnlyPrioritised(!onlyPrioritised)}
                    />
                  }
                />
              </Grid>
            </>
          )}
          {selectedSchemaTypeId !== ESchemaType.BaseSchema && (
            <Grid item xs sx={{ marginBottom: '15px' }}>
              <ActionButton
                icon={<PictureAsPdfIcon />}
                isLoading={isFetchingSchema}
                label={pdfButtonLabel}
                onClick={() => setIsPopupOpen(true)}
              />
            </Grid>
          )}
        </Grid>
        <Grid item xs>
          <Grid container columnSpacing={2} sx={{ marginBottom: '2em' }}>
            <ColorExplonationBox
              title={t('colorExplonation.default')}
              color={ECellColor.Default}
            />
            <ColorExplonationBox
              title={t('colorExplonation.important')}
              color={ECellColor.Important}
            />
            <ColorExplonationBox
              title={t('colorExplonation.required')}
              color={ECellColor.Required}
            />
            <ColorExplonationBox
              title={t('colorExplonation.disabled')}
              color={ECellColor.Disabled}
            />
            <ColorExplonationBox
              title={t('colorExplonation.outsideRange')}
              color={ECellColor.OutsideRange}
            />

            {selectedSchemaTypeId !== ESchemaType.BaseSchema &&
              selectedSchemaTypeId !== ESchemaType.PreviewSchema && (
                <>
                  <ColorExplonationBox
                    title={t('colorExplonation.user')}
                    color={ECellColor.User}
                  />
                  <ColorExplonationBox
                    title={t('colorExplonation.scene')}
                    color={ECellColor.Scene}
                  />
                  <ColorExplonationBox
                    title={t('colorExplonation.otherScene')}
                    color={ECellColor.OtherScene}
                  />
                </>
              )}

            {selectedSchemaTypeId === ESchemaType.PreviewSchema && (
              <ColorExplonationBox
                title={t('colorExplonation.rehearsal')}
                color={ECellColor.Scene}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
      {(selectedSchemaTypeId === ESchemaType.Scenes ||
        selectedSchemaTypeId === ESchemaType.SceneGroups) && (
        <Grid
          container
          columnSpacing={2}
          alignItems="center"
          justifyContent="center"
        >
          <Grid item xs>
            <b>{t('users')}: </b>
            <>
              {users.length ? (
                users
                  .map((user) => `${user.firstName} ${user.lastName}`)
                  .sort((a, b) => (a < b ? -1 : 1))
                  .join(', ')
              ) : (
                <i>
                  {onlyPrioritised ? t('noPrioritisedUsers') : t('noUsers')}
                </i>
              )}
            </>
          </Grid>
        </Grid>
      )}
      {!isLoadingSchemaData ? (
        <>
          {selectedSchemaTypeId === ESchemaType.BaseSchema && (
            <BaseSchema
              isLoading={isFetchingSchema}
              firstDay={firstDay}
              schemaData={schemaData}
              cellType={cellType}
              dayDatas={dayDatas}
              onGetData={() =>
                getSchemaQuery({ id: Number(urlParams.schemaId) })
              }
              setFirstDay={setFirstDay}
              setDayDatas={setDayDatas}
            />
          )}
          {selectedSchemaTypeId === ESchemaType.Scenes && (
            <ScenesSchema
              isLoading={isFetchingSchema}
              firstDay={firstDay}
              lastDay={lastDay}
              onlyPrioritised={onlyPrioritised}
              schemaData={schemaData}
              selectedSceneId={selectedId}
              onGetData={async () =>
                getSchemaQuery({ id: Number(urlParams.schemaId) })
              }
              setFirstDay={setFirstDay}
              placeDropDownData={placeDropDownData}
            />
          )}
          {selectedSchemaTypeId === ESchemaType.SceneGroups && (
            <SceneGroupsSchema
              isLoading={isFetchingSchema}
              firstDay={firstDay}
              lastDay={lastDay}
              schemaData={schemaData}
              selectedSceneGroupId={selectedId}
              onGetData={async () =>
                getSchemaQuery({ id: Number(urlParams.schemaId) })
              }
              setFirstDay={setFirstDay}
              placeDropDownData={placeDropDownData}
            />
          )}
          {selectedSchemaTypeId === ESchemaType.PreviewSchema && (
            <PreviewSchema
              isLoading={isFetchingSchema}
              firstDay={firstDay}
              lastDay={lastDay}
              schemaData={schemaData}
              setFirstDay={setFirstDay}
              placeDropDownData={placeDropDownData}
            />
          )}
        </>
      ) : (
        <ScenesSchema
          isLoading={isFetchingSchema}
          firstDay={firstDay}
          lastDay={lastDay}
          onlyPrioritised={onlyPrioritised}
          schemaData={schemaData}
          selectedSceneId={selectedId}
          onGetData={async () =>
            getSchemaQuery({ id: Number(urlParams.schemaId) })
          }
          setFirstDay={setFirstDay}
          placeDropDownData={placeDropDownData}
        />
      )}
      {schemaData && (
        <PopupDialog
          isOpen={isPopupOpen}
          title={
            <>
              {t('popups.createPdfTitle')}:{' '}
              <b>{schemaData?.schema.schema.name}</b>
            </>
          }
          content={
            <PdfSchema
              schema={schemaData}
              selectedScenes={
                selectedSchemaTypeId !== ESchemaType.PreviewSchema
                  ? schemaData && selectedSchemaTypeId === ESchemaType.Scenes
                    ? [selectedId]
                    : []
                  : undefined
              }
              selectedSceneGroups={
                selectedSchemaTypeId !== ESchemaType.PreviewSchema
                  ? schemaData &&
                    selectedSchemaTypeId === ESchemaType.SceneGroups
                    ? [selectedId]
                    : []
                  : undefined
              }
            />
          }
          disableMaxWidth
          isLoading={false}
          onClose={() => setIsPopupOpen(false)}
        />
      )}
    </>
  )
}

export default CreateFinalSchema
